import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import TrashIcon from 'icons/ic-trash.svg';
import RotateIcon from 'icons/rotate-right.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useAttachmentRotationProcessQuery } from 'components/hooks/attachmentRotationProcessQueries';
import { useCreateAttachmentRotationProcessMutation } from 'components/hooks/attachmentsMutation';

import { useBreadBoard } from 'components/contexts/Toaster';

import { getMember } from 'components/helpers/resources/attachments';

export default function Settings({
  attachment,
  onDelete,
  onRotateClick,
  onRotateSuccess,
}) {
  const { addInedibleToast } = useBreadBoard();
  const queryClient = useQueryClient();

  const createAttachmentRotationProcessMutation =
    useCreateAttachmentRotationProcessMutation();

  const [isDeleting, setIsDeleting] = useState(false);

  const { t: i18nTCommon } = useTranslation('common');

  const rotationProcessId =
    createAttachmentRotationProcessMutation.data?.data.id;

  const { data: domainRotationProcess, isError: isDomainRotationProcessError } =
    useAttachmentRotationProcessQuery({ rotationProcessId });

  const { attachedProcessed: isProcessed, filename, hashid } = attachment;

  const handleRotate = () => {
    createAttachmentRotationProcessMutation.mutate(
      { hashid },
      { onError: addInedibleToast },
    );
  };

  const handleRotationProcessReset = () => {
    createAttachmentRotationProcessMutation.reset();

    if (rotationProcessId) {
      queryClient.removeQueries({
        queryKey: ['attachments', 'rotationProcesses', rotationProcessId],
        exact: true,
      });
    }
  };

  if (domainRotationProcess?.data?.attributes?.status === 'completed') {
    const {
      data: {
        relationships: {
          attachment: {
            data: { id: attachmentId },
          },
        },
      },
      included,
    } = domainRotationProcess;

    onRotateSuccess({
      attachment,
      payload: getMember({
        attachment: included.attachment[attachmentId],
        coverImages: Object.values(included.coverImage),
      }),
    });

    handleRotationProcessReset();
  }

  if (
    isDomainRotationProcessError ||
    domainRotationProcess?.data?.attributes?.status === 'failed'
  ) {
    addInedibleToast();
    handleRotationProcessReset();
  }

  return (
    <div className='attachments__viewer-settings attachments__viewer-settings--compact-mode tw-gap-1'>
      <div className='attachments__viewer-filename'>
        <span className='truncated-text-container'>{filename}</span>
      </div>
      <div className='attachments__viewer-actions'>
        <button
          className={classNames(
            'attachments__viewer-rotate tw-mr-1 tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-p-0',
            !isProcessed ?
              'tw-pointer-events-none tw-bg-grey-050 [&_svg_path]:tw-fill-grey-300'
            : 'tw-bg-transparent tw-text-grey-500 hover:tw-bg-blue-025 hover:tw-text-grey-600 active:tw-bg-blue-050 active:tw-text-grey-600',
          )}
          disabled={!isProcessed}
          onClick={() => {
            handleRotate();
            onRotateClick({ attachment });
          }}
        >
          <RotateIcon fill='#2F3641' height={20} width={20} />
          <span className='tw-sr-only'>
            {i18nTCommon('attachments.rotate')}
          </span>
        </button>
        <button
          className={classNames(
            'attachments__viewer-delete tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-p-0',
            isDeleting ?
              'tw-pointer-events-none tw-bg-grey-050 [&_svg_path]:tw-fill-grey-300'
            : 'tw-bg-transparent hover:tw-bg-red-025 active:tw-bg-red-050 [&_svg_path]:tw-fill-grey-500 [&_svg_path]:hover:tw-fill-red-600 [&_svg_path]:active:tw-fill-red-600',
          )}
          disabled={isDeleting}
          onClick={() => {
            setIsDeleting(true);
            onDelete();
            handleRotationProcessReset();
          }}
        >
          <TrashIcon height={20} width={20} />
          <span className='tw-sr-only'>{i18nTCommon('shared.delete')}</span>
        </button>
      </div>
    </div>
  );
}

Settings.propTypes = {
  attachment: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRotateClick: PropTypes.func.isRequired,
  onRotateSuccess: PropTypes.func.isRequired,
};
